<template>
  <main class="physical-confirm-page">
    <AppNavBar :title="t('card.confirmOrder')" />

    <section class="address-card">
      <div class="address-card__line">
        <app-image class="address-card__icon" :src="locateIcon" alt="" />
        <span>{{ fullAddress }}</span>
      </div>
      <div class="address-card__meta">
        {{ draft.userName || "--" }} {{ draft.phoneNum || "" }}
      </div>
    </section>

    <section class="summary-card">
      <app-image :src="cardImage" alt="" />
      <div class="summary-card__row">
        <span>{{ t("card.cardType") }}</span>
        <div class="summary-card__value">{{ t("card.physicalCard") }}</div>
      </div>
      <div class="summary-card__divider" />
      <div class="summary-card__row">
        <span>{{ t("card.recentTradeScene") }}</span>
        <div class="summary-card__value">{{ t("card.physicalSceneDesc") }}</div>
      </div>
    </section>

    <footer class="submit-bar">
      <div class="submit-bar__price">
        <span>{{ t("card.applicationFeeLabel") }}</span>
        <div class="submit-bar__price-value">
          {{ draft.cardPrice || "0" }} {{ draft.cardPriceSymbol || "USD" }}
        </div>
      </div>
      <van-button
        class="submit-bar-button"
        round
        type="primary"
        :loading="submitting"
        @click="handlePayNow"
      >
        {{ t("card.payNow") }}
      </van-button>
    </footer>

    <PayPasswordPopup
      v-model:show="showPasswordDialog"
      v-model:password="passwordInput"
      :title="t('auth.verifyWalletPassword')"
      :placeholder="t('auth.enterPassword')"
      :confirm-text="t('common.confirm')"
      :forgot-text="t('auth.forgotPasswordRecovery')"
      show-forgot
      :confirm-disabled="!passwordInput.trim()"
      @cancel="closePasswordDialog(false)"
      @closed="handlePasswordPopupClosed"
      @forgot="handleForgotPassword"
      @confirm="submitPasswordDialog"
    />

    <van-popup
      v-model:show="showInsufficientBalanceDialog"
      round
      class="insufficient-dialog"
    >
      <section class="insufficient-dialog__panel">
        <button
          type="button"
          class="insufficient-dialog__close"
          @click="showInsufficientBalanceDialog = false"
        >
          <app-image
            class="app-cancel-icon"
            src="@/assets/images/cancel_gray.png"
            alt=""
          />
        </button>

        <div class="insufficient-dialog__icon">
          <app-image :src="hintIcon" alt="" />
        </div>

        <div class="insufficient-dialog__title">{{ t("common.tips") }}</div>
        <p class="insufficient-dialog__message">
          {{ t("card.currentCardInsufficientBalance") }}
        </p>

        <div class="insufficient-dialog__actions">
          <button
            type="button"
            class="insufficient-dialog__action insufficient-dialog__action--cancel"
            @click="showInsufficientBalanceDialog = false"
          >
            {{ t("common.cancel") }}
          </button>
          <button
            type="button"
            class="insufficient-dialog__action insufficient-dialog__action--confirm"
            @click="goTopup"
          >
            {{ t("card.topUpNow") }}
          </button>
        </div>
      </section>
    </van-popup>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { showFailToast } from "vant";
import { showPassedToast } from "@/utils/toast";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import PayPasswordPopup from "@/components/PayPasswordPopup.vue";
import { payPwdCheck } from "@/api/auth";
import { physicalPay } from "@/api/card";
import { useCardOrderStore } from "@/stores/card-order";
import { useProfileStore } from "@/stores/profile";
import { getLocalizedAreaName } from "@/utils/display-name";
import { navigateWithState } from "@/utils/route-state";
import locateIcon from "@/assets/images/cardImage/locate_icon.png";
import hintIcon from "@/assets/images/homeImage/hint_icon.png";
import defaultCardImage from "@/assets/images/cardImage/card_three.png";

const { t, locale } = useI18n();
const router = useRouter();
const cardOrderStore = useCardOrderStore();
const profileStore = useProfileStore();

const draft = ref({});
const submitting = ref(false);
const showPasswordDialog = ref(false);
const showInsufficientBalanceDialog = ref(false);
const passwordInput = ref("");
let passwordResolver = null;

const fullAddress = computed(() => {
  const country = getLocalizedAreaName(draft.value.country, locale.value);
  const province = getLocalizedAreaName(draft.value.province, locale.value);
  const city = getLocalizedAreaName(draft.value.city, locale.value);
  return (
    `${country}${province}${city}${draft.value.postalAddress || ""}` || "--"
  );
});
const cardImage = computed(
  () => draft.value.template?.image || defaultCardImage,
);

function verifyWalletPassword() {
  passwordInput.value = "";
  showPasswordDialog.value = true;
  return new Promise((resolve) => {
    passwordResolver = resolve;
  });
}

function closePasswordDialog(result = false) {
  if (passwordResolver) {
    passwordResolver(result);
    passwordResolver = null;
  }
  showPasswordDialog.value = false;
}

function handlePasswordPopupClosed() {
  passwordInput.value = "";
}

function handleForgotPassword() {
  closePasswordDialog(false);
  router.push("/auth/forgot-pay-password");
}

async function submitPasswordDialog() {
  const payPwd = String(passwordInput.value || "").trim();
  if (!payPwd) {
    showFailToast(t("auth.enterPassword"));
    return;
  }

  const result = await payPwdCheck({ payPwd });
  if (!result) {
    showFailToast(t("auth.passwordError"));
    return;
  }

  closePasswordDialog(true);
}

async function submitOrder() {
  const verified = await verifyWalletPassword();
  if (!verified) {
    return;
  }

  submitting.value = true;
  try {
    const result = await physicalPay({
      address: profileStore.platformAddress || null,
      phoneCode: draft.value.phoneCode,
      phoneNum: draft.value.phoneNum,
      postalCode: draft.value.postalCode,
      country: draft.value.country?.nameCn || draft.value.country?.nameEn || "",
      countryId: draft.value.country?.countryId || "",
      state: draft.value.province?.nameCn || draft.value.province?.nameEn || "",
      provinceId: draft.value.province?.provinceId || "",
      city: draft.value.city?.nameCn || draft.value.city?.nameEn || "",
      cityId: draft.value.city?.cityId || "",
      postalAddress: draft.value.postalAddress,
      userName: draft.value.userName,
      templateId: draft.value.templateId,
      cardId: draft.value.currentCardInfo?.id || "",
      pcbId:
        draft.value.currentCardInfo?.pcbId || draft.value.template?.pcbId || "",
    });

    if (String(result?.code || "") === "100019") {
      showInsufficientBalanceDialog.value = true;
      return;
    }

    cardOrderStore.mergeDraft({ paidAt: Date.now() });
    showPassedToast(t("card.orderPaid"));
    navigateWithState(router, "/card/physical-order/success", {
      namespace: "home-card",
      payload: {
        tab: "physical",
      },
    });
  } catch (error) {
    if (String(error?.code || "") === "100019") {
      showInsufficientBalanceDialog.value = true;
      return;
    }
    showFailToast(error?.msg || error?.message || t("common.retryLater"));
  } finally {
    submitting.value = false;
  }
}

function handlePayNow() {
  submitOrder();
}

function goTopup() {
  showInsufficientBalanceDialog.value = false;
  router.push("/wallet/topup");
}

onMounted(() => {
  cardOrderStore.hydrate();
  draft.value = cardOrderStore.draft || {};
  if (!draft.value?.templateId || !draft.value?.postalAddress) {
    navigateWithState(router, "/wallet/open-card", {
      namespace: "card-open",
      replace: true,
      payload: {
        type: "1",
      },
    });
  }
});
</script>

<style scoped lang="scss">
.physical-confirm-page {
  min-height: var(--app-viewport-height);
  padding: 14px 14px 110px;
  background: #f7f7f7;
}

.address-card,
.summary-card {
  border-radius: 8px;
  background: #ffffff;
  padding-bottom: 15px;
}

.address-card {
  padding: 16px;
}

.address-card__line {
  display: flex;
  align-items: flex-start;
  gap: 8px;

  .address-card__icon {
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    object-fit: contain;
  }

  span {
    color: #333333;
    font-size: 14px;
    line-height: 20px;
  }
}

.address-card__meta {
  margin-top: 10px;
  padding-left: 38px;
  color: #999999;
  font-size: 14px;
}

.summary-card {
  margin-top: 10px;
  overflow: hidden;

  .app-image {
    display: block;
    width: calc(100% - 24px);
    margin: 16px auto;
  }
}

.summary-card__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 24px;
  padding: 0 16px;
  color: #333333;
  font-size: 14px;
  line-height: 20px;

  .summary-card__value {
    color: #000000;
    font-weight: 500;
    text-align: right;
  }
}

.summary-card__divider {
  margin: 12px 16px 20px;
  border-top: 1px dashed #d9d9d9;
}

.submit-bar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  background: #ffffff;
  box-shadow: 0 -4px 18px rgba(0, 0, 0, 0.08);

  &-button {
    width: 92px;
    height: 32px;
    padding: 0;
    border-radius: 16px;
    font-weight: 700;

    :deep(.van-button__text) {
      font-size: 14px;
      line-height: 20px;
    }
  }
}

.submit-bar__price {
  display: flex;
  align-items: baseline;
  gap: 4px;
  white-space: nowrap;
  span,
  .submit-bar__price-value {
    display: block;
  }

  span {
    color: #333333;
    font-size: 13px;
  }

  .submit-bar__price-value {
    color: var(--van-primary-color);
    font-size: 16px;
    font-weight: bold;
  }
}

.insufficient-dialog {
  width: min(380px, calc(100vw - 48px));
  overflow: visible;
  background: transparent;
}

.insufficient-dialog__panel {
  position: relative;
  padding: 36px 20px 24px;
  border-radius: 20px;
  background: linear-gradient(180deg, #edf4ff 0%, #ffffff 44%);
  text-align: center;
}

.insufficient-dialog__close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 22px;
  height: 22px;
  padding: 0;
  background: transparent;
  border: 0;

  .app-image {
    display: block;
    width: 100%;
    height: 100%;
  }
}

.insufficient-dialog__icon {
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  background: linear-gradient(180deg, #4f8dff 0%, #2f67f7 100%);
  box-shadow: 0 8px 20px rgba(47, 103, 247, 0.24);

  .app-image {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
}

.insufficient-dialog__title {
  display: block;
  color: #2464ff;
  font-size: 18px;
  font-weight: 700;
}

.insufficient-dialog__message {
  color: #000;
  font-size: 16px;
  line-height: 28px;
  white-space: pre-line;
  padding: 10px 20px;
}

.insufficient-dialog__actions {
  display: flex;
  gap: 12px;
}

.insufficient-dialog__action {
  flex: 1;
  height: 50px;
  border: 0;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 500;
}

.insufficient-dialog__action--cancel {
  color: #4a76d9;
  background: linear-gradient(180deg, #deebff 0%, #cddfff 100%);
}

.insufficient-dialog__action--confirm {
  color: #ffffff;
  background: linear-gradient(180deg, #4c86ff 0%, #2a63f7 100%);
  box-shadow: 0 8px 16px rgba(42, 99, 247, 0.24);
}
</style>
